import { z } from 'zod';
import { BulkEditSelectionSchema } from './media-units-bulk-edit.js';
import { endpoint } from '../api/endpoint.js';

/**
 * "Delete selected" bulk action — soft-deletes a bulk selection of media units
 * (sets `is_deleted=true`, `deleted_at=now()`), mirroring the single-unit
 * `DELETE /api/media-units/:id` soft-delete semantics for every resolved id.
 *
 * Reuses `BulkEditSelectionSchema` verbatim (the same `{ ids } | { filter }`
 * union and 500-row `BULK_EDIT_CAP` as `bulk-edit`). Per-unit `media_unit.delete`
 * scope (mediaType + ownership) is enforced server-side; units the caller may
 * not delete are reported back in `failedIds` rather than aborting the call.
 *
 * Traceability: bulk-edit-media / Requirement: Bulk "delete selected" action.
 */
export const bulkDeleteRequestSchema = z.object({
  selection: BulkEditSelectionSchema,
});
export type BulkDeleteRequest = z.infer<typeof bulkDeleteRequestSchema>;

// Mirrors bulkRecomputeLocationResponseSchema's shape, using `deleted` for the
// count of units actually soft-deleted by this call.
export const bulkDeleteResponseSchema = z.object({
  deleted: z.number().int().min(0),
  failed: z.number().int().min(0),
  failedIds: z.array(z.number().int().positive()).optional(),
});
export type BulkDeleteResponse = z.infer<typeof bulkDeleteResponseSchema>;

export const bulkDeleteEndpoint = endpoint(
  'POST',
  '/media-units/bulk-delete',
  bulkDeleteRequestSchema,
  bulkDeleteResponseSchema,
);
